import { Component } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { AssignmentStorageService } from '../../storage/assignment-storage.service';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { IAssignment } from '../../models/assignment';


@Component({
    selector: 'app-assignment-report',
    templateUrl: './assignment-report.component.html',
    styleUrl: './assignment-report.component.scss',
    standalone: false
})
export class AssignmentReportComponent {
  
  constructor(
    private assignmentStore: AssignmentStorageService,
  ){}

  vm$ = combineLatest([
      this.assignmentStore.assignment$,
      this.assignmentStore.assignment$.pipe(
        map(assignment => {
          var availablePercentage = 0;
          var usedPercentage = 0;
          var totalSenconds = assignment.defaultTimeTotal.getTotalSeconds() - TimeSpan.parse(assignment.totalTime).getTotalSeconds();

          if(assignment.defaultTimeTotal.getTotalSeconds() > 0)
          {
            availablePercentage = Math.round((totalSenconds / assignment.defaultTimeTotal.getTotalSeconds()) * 100);
            usedPercentage = Math.round(100 - availablePercentage);
          }
        

          return {
            totalTimeDifference: TimeSpan.fromSeconds(totalSenconds),
            usedPercentage: usedPercentage,
            availablePercentage: availablePercentage
          }
        })
      )
    ]).pipe(
      map(([assignment, calculatedTimes]) => ({
        assignment,
        calculatedTimes,
        totals: this.calculateTotals(assignment),
        groupedByActivity: this.groupByActivity(assignment)
      }))
    )

      private groupByActivity(assignment: IAssignment): ActivityGroup[] {
    if (!assignment?.timeEntries?.length) {
      return [];
    }

    // Group by activity
    const activityMap = new Map<string, Map<string, EmployeeAggregate>>();
    const activityNames = new Map<string, string>();

    for (const entry of assignment.timeEntries) {
      const activityId = entry.assignmentService?.id || 'no-activity';
      const activityName = entry.assignmentService?.remarks || 'Ohne Aktivität';
      const employeeId = entry.employee?.id || 'no-employee';
      const employeeName = `${entry.employee?.firstName || ''} ${entry.employee?.lastName || ''}`.trim() || 'Unbekannt';

      activityNames.set(activityId, activityName);

      if (!activityMap.has(activityId)) {
        activityMap.set(activityId, new Map<string, EmployeeAggregate>());
      }

      const employeeMap = activityMap.get(activityId)!;
      const entryDate = new Date(entry.date);
      const minutes = this.parseDurationToMinutes(entry.duration);

      if (!employeeMap.has(employeeId)) {
        employeeMap.set(employeeId, {
          employeeId,
          employeeName,
          totalMinutes: 0,
          duration: '',
          costInternal: 0,
          costExternal: 0,
          dateFrom: entryDate,
          dateTo: entryDate
        });
      }

      const agg = employeeMap.get(employeeId)!;
      agg.totalMinutes += minutes;
      agg.costInternal += entry.costInternal || 0;
      agg.costExternal += entry.costExternal || 0;

      if (entryDate < agg.dateFrom) agg.dateFrom = entryDate;
      if (entryDate > agg.dateTo) agg.dateTo = entryDate;
    }

    // Convert to array structure
    const result: ActivityGroup[] = [];

    for (const [activityId, employeeMap] of activityMap) {
      const employees: EmployeeAggregate[] = [];
      let activityTotalMinutes = 0;
      let activityCostInternal = 0;
      let activityCostExternal = 0;

      for (const [, agg] of employeeMap) {
        agg.duration = this.formatMinutesToDuration(agg.totalMinutes);
        employees.push(agg);
        activityTotalMinutes += agg.totalMinutes;
        activityCostInternal += agg.costInternal;
        activityCostExternal += agg.costExternal;
      }

      result.push({
        activityId,
        activityName: activityNames.get(activityId) || '',
        employees,
        totalDuration: this.formatMinutesToDuration(activityTotalMinutes),
        totalCostInternal: activityCostInternal,
        totalCostExternal: activityCostExternal
      });
    }

    return result;
  }

  private parseDurationToMinutes(duration: string): number {
    if (!duration) return 0;
    const parts = duration.split(':');
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    return (hours * 60) + minutes;
  }

  private formatMinutesToDuration(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  }

  private calculateTotals(assignment: IAssignment) {
    if (!assignment?.timeEntries?.length) {
      return {
        duration: '00:00:00',
        costInternal: 0,
        costExternal: 0
      };
    }

    let totalMinutes = 0;
    let costInternal = 0;
    let costExternal = 0;

    for (const entry of assignment.timeEntries) {
      if (entry.duration) {
        const parts = entry.duration.split(':');
        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;
        totalMinutes += (hours * 60) + minutes;
      }
      costInternal += entry.costInternal || 0;
      costExternal += entry.costExternal || 0;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      duration: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`,
      costInternal,
      costExternal
    };
  }

   usedPercentageClass(percentage): string {
    if (percentage > 100) return 'error';
    if (percentage > 80) return 'warning';
    return '';
  }
}

interface EmployeeAggregate {
  employeeId: string;
  employeeName: string;
  totalMinutes: number;
  duration: string;
  costInternal: number;
  costExternal: number;
  dateFrom: Date;
  dateTo: Date;
}

interface ActivityGroup {
  activityId: string;
  activityName: string;
  employees: EmployeeAggregate[];
  totalDuration: string;
  totalCostInternal: number;
  totalCostExternal: number;
}